/**
 * Pose stills for the teach wizard.
 *
 * **Pose:** keys resolve against the vendored Pocket Yoga dictionary
 * (`poseAssets/` + `poseIndex.gen.js`). Authors can still use
 * **Image:** url | alt for any photo.
 *
 * SVG stick figures remain only as an offline / onError fallback.
 */

import { POSE_INDEX } from './poseIndex.gen';

const STROKE = '#2f5f50';
const MAT = '#c5d9cf';

/** Build a self-contained SVG data URI so <img> always has a visible source. */
export function svgDataUri(viewBox, paths, label) {
  const vb = String(viewBox || '0 0 120 120');
  const [, , w, h] = vb.split(/\s+/).map(Number);
  const width = w || 120;
  const height = h || 120;
  const pathEls = (paths || [])
    .map(
      (d) =>
        `<path d="${d}" fill="none" stroke="${STROKE}" stroke-width="3.8" ` +
        'stroke-linecap="round" stroke-linejoin="round"/>',
    )
    .join('');
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vb}" ` +
    `width="${width * 4}" height="${height * 4}" role="img" ` +
    `aria-label="${String(label || 'Pose').replace(/"/g, "'")}">` +
    `<rect width="100%" height="100%" fill="${MAT}" rx="12"/>` +
    pathEls +
    '</svg>';
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** Minimal stick figure used when a remote / local image fails to load. */
const FALLBACK_PATHS = [
  'M60 24 m-8 0 a8 8 0 1 1 16 0 a8 8 0 1 1 -16 0',
  'M60 34 L60 68',
  'M38 50 L60 44 L82 50',
  'M60 68 L46 104',
  'M60 68 L74 104',
];

// Webpack bundles every PNG under poseAssets/ and gives us hashed URLs.
const poseAssetCtx = require.context('./poseAssets', false, /\.png$/);
const ASSET_URL_BY_FILE = Object.fromEntries(
  poseAssetCtx.keys().map((key) => {
    const file = key.replace(/^\.\//, '');
    return [file, poseAssetCtx(key)];
  }),
);

function assetUrl(imageFile) {
  if (!imageFile) return '';
  return ASSET_URL_BY_FILE[imageFile] || ASSET_URL_BY_FILE[`./${imageFile}`] || '';
}

function entryFromIndex(pose) {
  if (!pose) return null;
  const url = assetUrl(pose.imageFile);
  if (!url) return null;
  const sanskrit = (pose.sanskrit || [])[0];
  const alt = sanskrit
    ? `${pose.displayName} (${sanskrit})`
    : pose.displayName;
  return {
    kind: 'img',
    alt,
    url,
    source: 'poseAssets',
    pocketName: pose.name,
    meta: pose,
  };
}

/** Short lesson-cue keys → Pocket Yoga dictionary `name`. */
export const POSE_SHORTCUTS = {
  easy: 'Easy',
  mountain: 'Mountain',
  upwardSalute: 'MountainSalute',
  chair: 'Chair',
  cat: 'Cat',
  cow: 'Dog', // Pocket Yoga names Bitilasana "Dog" (display: Cow)
  downwardDog: 'DownwardDog',
  lowLunge: 'WarriorIKneeling',
  warrior2: 'WarriorII',
  triangle: 'TriangleForward',
  tree: 'Tree',
  cobra: 'Cobra',
  bridge: 'Bridge',
  child: 'ChildTraditional',
  boat: 'BoatFull',
  fish: 'FishPreparation',
  reclinedBoundAngle: 'SupineBoundAngle',
  corpse: 'Corpse',
  sidePlank: 'PlankSide',
  forwardFold: 'ForwardBend',
  staff: 'Staff',
  seatedFold: 'SeatedForwardBend',
  wideFold: 'WideLeggedForwardBendI',
  janu: 'HeadToKnee',
  twist: 'SupineBellyTwist',
  legsUp: 'CorpseDoubleLegRaise',
  warrior1: 'WarriorI',
  plank: 'Plank',
  tabletop: 'BoxNeutral',
};

function norm(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '');
}

const BY_NAME = Object.fromEntries(POSE_INDEX.map((p) => [p.name, p]));
const BY_NORM = new Map();

function addNorm(key, pose) {
  const n = norm(key);
  if (!n || BY_NORM.has(n)) return;
  BY_NORM.set(n, pose);
}

for (const pose of POSE_INDEX) {
  addNorm(pose.name, pose);
  addNorm(pose.displayName, pose);
  addNorm(pose.sortName, pose);
  for (const a of pose.aka || []) addNorm(a, pose);
  for (const s of pose.sanskrit || []) addNorm(s, pose);
}
for (const [short, name] of Object.entries(POSE_SHORTCUTS)) {
  addNorm(short, BY_NAME[name]);
}

/** Compact lookup used by older call sites — short keys only. */
export const POSE_LIBRARY = Object.fromEntries(
  Object.entries(POSE_SHORTCUTS)
    .map(([key, name]) => {
      const entry = entryFromIndex(BY_NAME[name]);
      return entry ? [key, entry] : null;
    })
    .filter(Boolean)
    .concat([[
      'lion',
      {
        kind: 'img',
        alt: 'Lion pose (Simhasana)',
        url: svgDataUri(
          '0 0 120 120',
          [
            'M60 28 m-8 0 a8 8 0 1 1 16 0 a8 8 0 1 1 -16 0',
            'M60 38 L60 62',
            'M40 72 L60 50 L80 72',
            'M44 104 L52 78 L60 62 L68 78 L76 104',
            'M52 30 L48 22',
            'M68 30 L72 22',
          ],
          'Lion pose (Simhasana)',
        ),
        source: 'svg',
      },
    ]]),
);

const FILE_BASE = 'https://commons.wikimedia.org/wiki/Special:FilePath';

export function poseImageUrl(file, width = 720) {
  const name = String(file || '').trim();
  if (!name) return '';
  return `${FILE_BASE}/${encodeURIComponent(name)}?width=${width}`;
}

/**
 * Phrase → library key. Longer phrases first so "warrior ii" beats "warrior".
 * Used when a step has no **Pose:** line (stale Firestore cues, free text).
 */
const POSE_ALIASES = [
  ['downward-facing dog', 'downwardDog'],
  ['downward facing dog', 'downwardDog'],
  ['downward dog', 'downwardDog'],
  ['down dog', 'downwardDog'],
  ['adho mukha', 'downwardDog'],
  ['upward salute', 'upwardSalute'],
  ['raised arms', 'upwardSalute'],
  ['low lunge', 'lowLunge'],
  ['crescent lunge', 'lowLunge'],
  ['warrior ii', 'warrior2'],
  ['warrior 2', 'warrior2'],
  ['warrior two', 'warrior2'],
  ['virabhadrasana ii', 'warrior2'],
  ['warrior i', 'warrior1'],
  ['warrior 1', 'warrior1'],
  ['warrior one', 'warrior1'],
  ['virabhadrasana i', 'warrior1'],
  ['side plank', 'sidePlank'],
  ['vasisthasana', 'sidePlank'],
  ['forward fold', 'forwardFold'],
  ['uttanasana', 'forwardFold'],
  ['wide-legged', 'wideFold'],
  ['wide fold', 'wideFold'],
  ['prasarita', 'wideFold'],
  ['seated fold', 'seatedFold'],
  ['paschimottanasana', 'seatedFold'],
  ['reclined bound', 'reclinedBoundAngle'],
  ['bound angle', 'reclinedBoundAngle'],
  ['supine baddha', 'reclinedBoundAngle'],
  ['legs up', 'legsUp'],
  ['viparita karani', 'legsUp'],
  ['easy seat', 'easy'],
  ['sukhasana', 'easy'],
  ['child’s pose', 'child'],
  ["child's pose", 'child'],
  ['child pose', 'child'],
  ['balasana', 'child'],
  ['mountain', 'mountain'],
  ['tadasana', 'mountain'],
  ['chair pose', 'chair'],
  ['utkatasana', 'chair'],
  ['tabletop', 'tabletop'],
  ['table top', 'tabletop'],
  ['cat / cow', 'cow'],
  ['cat/cow', 'cow'],
  ['cat-cow', 'cow'],
  ['marjaryasana', 'cat'],
  ['bitilasana', 'cow'],
  ['triangle', 'triangle'],
  ['trikonasana', 'triangle'],
  ['tree pose', 'tree'],
  ['vrksasana', 'tree'],
  ['cobra', 'cobra'],
  ['bhujangasana', 'cobra'],
  ['bridge', 'bridge'],
  ['setu bandha', 'bridge'],
  ['corpse', 'corpse'],
  ['savasana', 'corpse'],
  ['boat', 'boat'],
  ['navasana', 'boat'],
  ['fish pose', 'fish'],
  ['matsyasana', 'fish'],
  ['lion', 'lion'],
  ['simhasana', 'lion'],
  ['staff pose', 'staff'],
  ['dandasana', 'staff'],
  ['janu', 'janu'],
  ['twist', 'twist'],
  ['plank', 'plank'],
  ['cow', 'cow'],
  ['cat', 'cat'],
  ['tree', 'tree'],
  ['chair', 'chair'],
  ['child', 'child'],
  ['fish', 'fish'],
  ['easy', 'easy'],
];

/**
 * Resolve a **Pose:** key into a display descriptor:
 * { kind:'img', url, alt }
 */
export function resolvePoseRef(ref) {
  const key = String(ref || '').trim();
  if (!key) return null;

  if (/^https?:\/\//i.test(key) || key.startsWith('data:image/')) {
    return { kind: 'img', url: key, alt: 'Pose reference' };
  }
  if (/\.(jpe?g|png|webp|gif)$/i.test(key) && !ASSET_URL_BY_FILE[key]) {
    // Bare filename in poseAssets, or Wikimedia fallback.
    const local = assetUrl(key) || assetUrl(key.split('/').pop());
    if (local) {
      return {
        kind: 'img',
        url: local,
        alt: key.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' '),
        source: 'poseAssets',
      };
    }
    return {
      kind: 'img',
      url: poseImageUrl(key),
      alt: key.replace(/^Mr-yoga-/i, '').replace(/[-_]/g, ' '),
    };
  }

  // Short lesson keys first.
  if (POSE_LIBRARY[key]) return { ...POSE_LIBRARY[key] };
  const lowerKey = key.toLowerCase().replace(/\s+/g, '');
  if (POSE_LIBRARY[lowerKey]) return { ...POSE_LIBRARY[lowerKey] };

  // Full dictionary: Pocket Yoga name, display name, aka, sanskrit.
  const fromIndex = BY_NAME[key] || BY_NORM.get(norm(key));
  if (fromIndex) {
    const entry = entryFromIndex(fromIndex);
    if (entry) return entry;
  }

  // Natural-language pose names in **Pose:** lines
  const inferred = inferPoseKeys(key);
  if (inferred.length && POSE_LIBRARY[inferred[0]]) {
    return { ...POSE_LIBRARY[inferred[0]] };
  }

  return null;
}

function includesPhrase(hay, phrase) {
  let from = 0;
  while (from <= hay.length) {
    const i = hay.indexOf(phrase, from);
    if (i < 0) return false;
    const before = hay[i - 1];
    const after = hay[i + phrase.length];
    const edge = (ch) => ch == null || /[^a-z0-9]/.test(ch);
    if (edge(before) && edge(after)) return true;
    from = i + 1;
  }
  return false;
}

/** @returns {string[]} library keys mentioned in free text */
export function inferPoseKeys(text) {
  const hay = String(text || '').toLowerCase();
  if (!hay.trim()) return [];
  const found = [];
  const seen = new Set();
  for (const [phrase, key] of POSE_ALIASES) {
    if (seen.has(key) || !POSE_LIBRARY[key]) continue;
    if (!includesPhrase(hay, phrase)) continue;
    seen.add(key);
    found.push(key);
    if (found.length >= 3) break;
  }
  return found;
}

/** Attach diagrams when a step has no explicit **Pose:** / **Image:** lines. */
export function ensureStepImages(step) {
  if (!step) return step;
  if (Array.isArray(step.images) && step.images.length) return step;
  const keys = inferPoseKeys(`${step.title || ''}\n${step.body || ''}`);
  if (!keys.length) return step;
  return {
    ...step,
    images: keys.map((k) => resolvePoseRef(k)).filter(Boolean),
  };
}

/** Generic silhouette when a photo fails. */
export function fallbackPoseImage(alt = 'Pose reference') {
  return {
    kind: 'img',
    alt,
    url: svgDataUri('0 0 120 120', FALLBACK_PATHS, alt),
  };
}

/** Look up dictionary metadata by Pocket Yoga name or short key. */
export function lookupPose(ref) {
  const key = String(ref || '').trim();
  if (!key) return null;
  if (POSE_SHORTCUTS[key]) return BY_NAME[POSE_SHORTCUTS[key]] || null;
  return BY_NAME[key] || BY_NORM.get(norm(key)) || null;
}

export function listPoses({ visibility, category, difficulty, query } = {}) {
  const q = norm(query);
  return POSE_INDEX.filter((p) => {
    if (visibility && p.visibility !== visibility) return false;
    if (category && p.category !== category) return false;
    if (difficulty && p.difficulty !== difficulty) return false;
    if (!q) return true;
    const blob = norm([
      p.name,
      p.displayName,
      p.sortName,
      ...(p.aka || []),
      ...(p.sanskrit || []),
    ].join(' '));
    return blob.includes(q);
  });
}

export function poseThumb(poseOrName) {
  const pose = typeof poseOrName === 'string'
    ? lookupPose(poseOrName)
    : poseOrName;
  return entryFromIndex(pose);
}

/** Poses whose `imageFile` is not present in the webpack poseAssets map. */
export function listPosesMissingImages() {
  return POSE_INDEX.filter((p) => !assetUrl(p.imageFile));
}
